import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const orgChartApi = createApi({
  reducerPath: "orgChartApi",
  keepUnusedDataFor: 300,
  tagTypes: ["OrgChart"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getMyOrgChart: builder.query({
      query: (year) => ({
        url: `/orgchart/portal/my-downline?year=${year}`,
        method: "GET",
      }),
      providesTags: (_result, _error, year) => [{ type: "OrgChart", id: year }],
    }),
    getMyTeamChart: builder.query({
      // Backwards-compatible: existing callers pass a bare year (number);
      // President / VP of Sales pass { year, team } so the BFF returns the
      // chosen team instead of the caller's own. The old shape continues to
      // work because the BFF treats a missing `team` as "use your own".
      query: (arg) => {
        const year = typeof arg === "object" && arg !== null ? arg.year : arg;
        const team = typeof arg === "object" && arg !== null ? arg.team : undefined;
        const params = new URLSearchParams({ year: String(year ?? "") });
        if (team) params.set("team", team);
        return { url: `/orgchart/portal/my-team-chart?${params.toString()}`, method: "GET" };
      },
      providesTags: (_result, _error, arg) => {
        const year = typeof arg === "object" && arg !== null ? arg.year : arg;
        const team = typeof arg === "object" && arg !== null ? arg.team : undefined;
        return [{ type: "OrgChart", id: team ? `${year}-${team}` : year }];
      },
    }),
    getAttendanceCount: builder.query({
      query: ({ year, repId }) => ({
        url: `/events/portal/attendance/count?year=${year}&repId=${encodeURIComponent(repId ?? "")}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [{ type: "OrgChart", id: `attendance-count-${arg?.year}-${arg?.repId ?? "none"}` }],
    }),
    getOrgChartEvents: builder.query({
      query: ({ year, recId, repId }) => ({
        url: `/events/portal/?recId=${encodeURIComponent(recId ?? "")}&repId=${encodeURIComponent(repId ?? recId ?? "")}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [{ type: "OrgChart", id: `events-${arg?.year}-${arg?.repId ?? arg?.recId ?? "none"}` }],
    }),
    updateEventAttendance: builder.mutation({
      query: (payload) => ({
        url: "/events/portal/attendance",
        method: "POST",
        body: payload,
      }),
    }),
    updateOrgChartManagementNotes: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/management-notes",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: "OrgChart", id: Number(payload?.year) },
      ],
    }),
    updateDriverAssignment: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/update-driver-assignment",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: "OrgChart", id: Number(payload?.year) },
      ],
    }),
    updateApartmentAssignment: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/update-apartment-assignment",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: "OrgChart", id: Number(payload?.year) },
      ],
    }),
    transferRepApartment: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/transfer-rep-apartment",
        method: "POST",
        body: {...payload, fromRepApartmentId: "rechQaAY0RuUCjtnZ"},
      }),
    }),
    getApartmentDetails: builder.query({
      query: (apartmentKey) => ({
        url: `/orgchart/portal/apartment-details/${encodeURIComponent(apartmentKey)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [{ type: "OrgChart", id: `apartment-${arg}` }],
    }),
    updateNegativeStatus: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/update-negative-status",
        method: "POST",
        body: payload,
      }),
    }),
    getRepFinalStatusOptions: builder.query({
      query: () => ({
        url: "/orgchart/portal/final-status-options",
        method: "GET",
      }),
      providesTags: [{ type: "OrgChart", id: "final-status-options" }],
    }),
    updateRepStatus: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/update-rep-status",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: "OrgChart", id: Number(payload?.year) },
      ],
    }),
    getRepTeamOptions: builder.query({
      query: () => ({ url: "/orgchart/portal/team-options", method: "GET" }),
      providesTags: [{ type: "OrgChart", id: "team-options" }],
    }),
    // Rep Card "Team" save (thread #1580). The team chart for every team is
    // invalidated too, since the rep leaves one team and joins another.
    updateRepTeam: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/update-rep-team",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: "OrgChart", id: Number(payload?.year) },
        "OrgChart",
      ],
    }),
    sendRepLogin: builder.mutation({
      query: (payload) => ({
        url: "/orgchart/portal/send-login",
        method: "POST",
        body: payload,
      }),
    }),
    getTrainingCompletion: builder.query({
      query: ({ year, repId }) => ({
        url: `/orgchart/portal/training-completion?year=${year}&repId=${encodeURIComponent(repId ?? "")}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [{ type: "OrgChart", id: `training-completion-${arg?.year}-${arg?.repId ?? "none"}` }],
    }),
    getRepWatchTimeTotals: builder.query({
      query: ({ recid, year }) => ({
        url: `/orgchart/portal/training-time-totals?recid=${encodeURIComponent(recid ?? "")}&year=${encodeURIComponent(year ?? new Date().getFullYear())}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [{ type: "OrgChart", id: `watch-time-${arg?.recid ?? "none"}-${arg?.year ?? "none"}` }],
    }),
    getRepSeasonHistory: builder.query({
      query: ({ recid }) => ({
        url: `/orgchart/portal/rep-season-history?recid=${encodeURIComponent(recid ?? "")}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [{ type: "OrgChart", id: `season-history-${arg?.recid ?? "none"}` }],
    }),
    fetchEmployeeEvents: builder.query({
      query: (userRecordId) => ({
        url: `/task/portal/fetch-employee-events/${encodeURIComponent(userRecordId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "OrgChart", id: `employee-events-${id}` }],
    }),
    getEmployeeLeadCount: builder.query({
      query: ({ employeeIds, year }) => ({
        url: `/user/portal/employee-lead-count?employeeIds=${encodeURIComponent(employeeIds.join(","))}&year=${year}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetMyOrgChartQuery,
  useGetMyTeamChartQuery,
  useGetAttendanceCountQuery,
  useGetOrgChartEventsQuery,
  useUpdateEventAttendanceMutation,
  useUpdateOrgChartManagementNotesMutation,
  useUpdateDriverAssignmentMutation,
  useUpdateApartmentAssignmentMutation,
  useTransferRepApartmentMutation,
  useGetApartmentDetailsQuery,
  useGetTrainingCompletionQuery,
  useGetRepWatchTimeTotalsQuery,
  useGetRepSeasonHistoryQuery,
  useGetEmployeeLeadCountQuery,
  useUpdateNegativeStatusMutation,
  useFetchEmployeeEventsQuery,
  useGetRepFinalStatusOptionsQuery,
  useUpdateRepStatusMutation,
  useGetRepTeamOptionsQuery,
  useUpdateRepTeamMutation,
  useSendRepLoginMutation,
} =
  orgChartApi;
